import { forwardRef, InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    className?: string;
    placeholder?: string;
    error?: boolean;
    helperText?: string;
    showClearButton?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ className = '', error, helperText, showClearButton, ...props }, ref) => {
        const baseClasses =
            'flex h-10 w-full rounded-md border border-input bg-background px-3 ring-offset-background';
        const fileClasses = 'file:border-0 file:bg-transparent file:text-sm file:font-medium';
        const placeholderClasses = 'placeholder:text-muted-foreground';
        const focusClasses =
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kotomilThemaColor';
        const disabledClasses = 'disabled:cursor-not-allowed disabled:opacity-50';
        const errorClasses = error ? 'border-red-500 focus-visible:ring-red-500' : '';
        const clearButtonPadding = showClearButton && props.value ? 'pr-10' : '';

        const inputClasses = `${baseClasses} ${fileClasses} ${placeholderClasses} ${focusClasses} ${disabledClasses} ${errorClasses} ${clearButtonPadding} ${className}`;

        const placeholder = props.placeholder || '';

        const handleClear = () => {
            if (props.onChange) {
                const syntheticEvent = {
                    target: {
                        value: '',
                        name: props.name,
                    },
                } as React.ChangeEvent<HTMLInputElement>;
                props.onChange(syntheticEvent);

                if (ref && typeof ref !== 'function' && ref.current) {
                    ref.current.focus();
                }
            }
        };

        /**
         * レンダリング
         */
        return (
            <div className="w-full">
                <div className="relative">
                    <input
                        className={inputClasses}
                        placeholder={placeholder}
                        autoComplete="on"
                        ref={ref}
                        {...props}
                    />
                    {showClearButton && props.value && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute inset-y-0 right-0 flex items-center pr-3"
                            aria-label="Clear input"
                        >
                            <svg
                                className="h-4 w-4 text-gray-400 hover:text-gray-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    )}
                </div>
                {helperText && (
                    <p className={`mt-1 text-xs ${error ? 'text-red-500' : 'text-gray-500'}`}>
                        {helperText}
                    </p>
                )}
            </div>
        );
    },
);

Input.displayName = 'Input';

export { Input };
